// MediaComponent.js
import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  deleteMediaAction,
  getFeaturedMediaAction,
  getLexicaAIDataAction,
  getMediaDataAction,
  getUnsplashDataAction,
} from 'store/actions/media.action'
import { Dropdown, FilterTags, MasonryLayout } from 'components/index.js'
import useIntersectionObserver from 'hooks/useInfiniteScroll'
import { filterMediaTabs } from 'utils/constants'
import { downloadFile } from 'utils/fileUtils'
import { copyToClipboard, compareExtWithFileType } from 'utils/mediaUtils'
import { useRouter } from 'next/router'
import MediaItem from './MediaItem'
import MediaSkeleton from './MediaSkeleton'
import { CircularProgress } from '@mui/material'

const fileTypes = [
  { label: 'Image', value: 'Image' },
  { label: 'Video', value: 'Video' },
  { label: 'Audio', value: 'Audio' },
  { label: '3D Image', value: '3D Image' },
  { label: 'JSON', value: 'JSON' },
]
const MediaComponent = ({ onClickMedia, defaultFilter = 'Trending', classNames = '', filterClassNames = '' }) => {
  const dispatch = useDispatch()
  const router = useRouter()

  const user = useSelector((state) => state.auth.user)
  const newMediaAdded = useSelector((state) => state.media.newMediaAdded)
  const searchState = useSelector((state) => state.header.search)

  const { query, isReady } = router // Destructure isReady from the router

  const rowsNo = 30

  // Initialize filter and search as null
  const [filter, setFilter] = useState(null)
  const [fileTypeFilters, setFileTypeFilters] = useState([])
  const [search, setSearch] = useState(null)

  const [loading, setLoading] = useState(false)
  const [generatedMedia, setGeneratedMedia] = useState(null)
  const [uploadedMedia, setUploadedMedia] = useState(null)
  const [featuredMedia, setFeaturedMedia] = useState(null)
  const [unsplashMedia, setUnsplashMedia] = useState(null)
  const [lexicaMedia, setLexicaMedia] = useState(null)

  const [generatedPage, setGeneratedPage] = useState(0)
  const [uploadedPage, setUploadedPage] = useState(0)
  const [featuredPage, setFeaturedPage] = useState(0)
  const [unsplashPage, setUnsplashPage] = useState(0)
  const [lexicaPage, setLexicaPage] = useState(0)

  const [ref, { entry }] = useIntersectionObserver()
  const isVisible = useMemo(() => entry && entry.isIntersecting, [entry])

  // Update filter and search when router is ready
  useEffect(() => {
    if (isReady) {
      setFilter(query.filter || defaultFilter)
      setSearch(query.search || searchState || '')
    }
  }, [isReady, query.filter, query.search, defaultFilter, searchState])
  // Memoize functions to prevent unnecessary re-renders
  const handleMediaClick = useCallback(
    (item) => {
      if (onClickMedia) {
        onClickMedia(item.url)
      } else {
        router.push(
          {
            pathname: router.pathname,
            query: {
              ...router.query,
              modalType: 'editor',
              mediaUrl: item.url,
              tags: item.tags?.toLowerCase(),
            },
          },
          undefined,
          { shallow: true }
        )
      }
    },
    [onClickMedia, router]
  )

  const handleImageDownload = useCallback((url) => {
    if (url) downloadFile(url)
  }, [])

  const handleUrlCopy = useCallback((url) => dispatch(copyToClipboard(url)), [dispatch])

  const handleChangeFilter = useCallback(
    (value) => {
      switch (value) {
        case 'My Results':
          setGeneratedMedia(null)
          setGeneratedPage(0)
          break
        case 'My Uploads':
          setUploadedMedia(null)
          setUploadedPage(0)
          break
        case 'Unsplash':
          setUnsplashMedia(null)
          setUnsplashPage(0)
          break
        case 'Lexica':
          setLexicaMedia(null)
          setLexicaPage(0)
          break
        default:
          setFeaturedMedia(null)
          setFeaturedPage(0)
      }
      setFilter(value)
      router.push(
        {
          pathname: router.pathname,
          query: { ...router.query, filter: value },
        },
        undefined,
        { shallow: true }
      )
    },
    [router]
  )
  const updatePages = useCallback((setMedia, setPage, defaultPage, mediaPage, oldMediaList, response) => {
    setMedia([...(defaultPage ? [] : oldMediaList), ...(response.media || [])])
    if (!Math.floor(((defaultPage || mediaPage + 1) * 30) / response.count)) setPage(defaultPage || mediaPage + 1)
    else setPage(-1)
  }, [])

  const loadData = useCallback(
    async (page, tab, searchValue) => {
      if (loading == tab || !tab) return
      setLoading(tab)
      const fromPage = page ? page - 1 : null
      const searchingValue = searchValue || (['Trending', 'Lexica', 'Unsplash'].includes(tab) ? '' : tab)
      if (tab === 'My Results') {
        if (generatedPage === -1 && page !== 1) return
        const response = await dispatch(
          getMediaDataAction(
            'deployment-result',
            (fromPage || generatedPage) * rowsNo,
            (fromPage || generatedPage) * rowsNo + rowsNo
          )
        )
        updatePages(setGeneratedMedia, setGeneratedPage, page, generatedPage, generatedMedia, response)
      } else if (tab === 'My Uploads') {
        if (uploadedPage === -1 && page !== 1) return
        const response = await dispatch(
          getMediaDataAction('', (fromPage || uploadedPage) * rowsNo, (fromPage || uploadedPage) * rowsNo + rowsNo)
        )
        updatePages(setUploadedMedia, setUploadedPage, page, uploadedPage, uploadedMedia, response)
      } else if (tab === 'Unsplash') {
        if (unsplashPage === -1 && page !== 1) return
        const response = await getUnsplashDataAction(page || unsplashPage + 1, searchingValue)
        setUnsplashMedia([...(page ? [] : unsplashMedia), ...response.media])
        if (response.count === 30) setUnsplashPage(page || unsplashPage + 1)
        else setUnsplashPage(-1)
      } else if (lexicaPage !== -1 && tab === 'Lexica') {
        if (lexicaPage === -1 && page !== 1) return
        const response = await getLexicaAIDataAction(searchingValue)
        setLexicaMedia([...(page ? [] : lexicaMedia || []), ...(response?.media || [])])
        setLexicaPage(-1)
      } else if (featuredPage !== -1 || page) {
        const response = await getFeaturedMediaAction(page || featuredPage + 1, filter === 'Trending' ? '' : filter)
        updatePages(setFeaturedMedia, setFeaturedPage, page, featuredPage, featuredMedia, response)
      }
      setTimeout(() => {
        setLoading(false)
      }, 400)
    },
    [
      loading,
      setLoading,
      dispatch,
      getMediaDataAction,
      getUnsplashDataAction,
      getLexicaAIDataAction,
      getFeaturedMediaAction,
      updatePages,
      generatedPage,
      uploadedPage,
      unsplashPage,
      lexicaPage,
      featuredPage,
      generatedMedia,
      uploadedMedia,
      unsplashMedia,
      lexicaMedia,
      featuredMedia,
      rowsNo,
      filter,
    ]
  )

  // Load data when filter or search changes and router is ready
  useEffect(() => {
    if (filter) {
      loadData(1, filter, search)
    }
  }, [filter, search])

  // Infinite scrolling: Load more data when the ref is visible
  useEffect(() => {
    if (!loading && isVisible && (uploadedMedia || featuredMedia || unsplashMedia || lexicaMedia || generatedMedia)) {
      loadData(null, filter, search)
    }
  }, [isVisible])

  // Redirect to 'Trending' tab when new media is added and filter is not 'Trending'
  useEffect(() => {
    if (newMediaAdded && filter !== 'Trending') {
      router.push(
        {
          pathname: router.pathname,
          query: { ...router.query, filter: 'Trending' },
        },
        undefined,
        { shallow: true }
      )
    }
  }, [newMediaAdded])

  const renderMediaArray = useMemo(() => {
    switch (filter) {
      case 'My Results':
        return generatedMedia
      case 'My Uploads':
        return uploadedMedia
      case 'Unsplash':
        return unsplashMedia
      case 'Lexica':
        return lexicaMedia
      default:
        return featuredMedia
    }
  }, [filter, generatedMedia, uploadedMedia, unsplashMedia, lexicaMedia, featuredMedia])

  return (
    <div className="w-full media">
      <div>
        <div className={`flex flex-col gap-3 justify-between items-center w-full md:flex-row ${classNames}`}>
          <FilterTags
            tagsList={
              user ? filterMediaTabs : filterMediaTabs.filter((item) => !['My Uploads', 'My Results'].includes(item))
            }
            activeTab={filter}
            setActiveTab={(value) => {
              handleChangeFilter(value)
            }}
            classNames={filterClassNames}
          />
          <Dropdown
            options={fileTypes}
            selectedOptions={fileTypeFilters}
            onSelect={(value) => {
              setFileTypeFilters((prevSelected) => {
                const updatedPublishers = prevSelected.includes(value)
                  ? prevSelected.filter((source) => source !== value)
                  : [...prevSelected, value]
                console.log(updatedPublishers)
                return updatedPublishers
              })
            }}
            handleAllSelect={(value) =>
              setFileTypeFilters(() => (value ? fileTypes.map((option) => option.value) : []))
            }
            dropdownLabel={'Media Type'}
            multipleSelect={true}
            dropdownMenuStyle={'!left-auto min-h-[200px]'}
            dropContainerStyle={'md:w-[220px] max-w-[220px] mt-3 md:mt-0'}
          />
        </div>
        <div className="main-wrapper">
          <MasonryLayout columnClass="galleryContainer my-[10px] justify-center h-full pb-[220px] relative w-full">
            {renderMediaArray?.map(
              (item, index) =>
                (!fileTypeFilters.length ||
                  fileTypeFilters.length === 5 ||
                  compareExtWithFileType(fileTypeFilters, item.url)) && (
                  <MediaItem
                    key={index}
                    item={item}
                    index={index}
                    handleMediaClick={handleMediaClick}
                    handleImageDownload={handleImageDownload}
                    handleUrlCopy={handleUrlCopy}
                    onDeleteClick={(id) => {
                      dispatch(
                        deleteMediaAction(id, () => {
                          loadData(1, filter, search)
                        })
                      )
                    }}
                  />
                )
            )}
            {!renderMediaArray?.length &&
              loading &&
              Array(20)
                .fill(0)
                .map((_, index) => <MediaSkeleton key={`skeleton-${index}`} index={index} />)}
            {!renderMediaArray?.length && !loading && (
              <div className="flex items-center justify-center w-full h-64">
                <p className="text-gray-500">No media found</p>
              </div>
            )}
          </MasonryLayout>
        </div>
        {(!['My Uploads', 'My Results', 'Unsplash', 'Lexica'].includes(filter) ||
          (filter === 'My Results' && generatedPage !== -1) ||
          (filter === 'My Uploads' && uploadedPage !== -1) ||
          (filter === 'Unsplash' && unsplashPage !== -1) ||
          (filter === 'Lexica' && lexicaPage !== -1)) && <div ref={ref} className="w-full h-[30px]" />}

        {loading && renderMediaArray?.length > 0 && (
          <div className="flex items-center justify-center w-full py-8">
            <CircularProgress color="inherit" />
          </div>
        )}
      </div>
    </div>
  )
}

export default MediaComponent
